import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  input,
  output,
  QueryList,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { DateTime } from 'luxon';
import { HumanDatePipe } from '../../../pipes/humandate.pipe';
import { TemplateForDirective } from './template-for.directive';
import { InputComponent } from '../../../forms/input/input.component';

export interface IColumnDef {
  headerText: string;
  field?: string;
  columnType?: string;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc';
  widthZero?: boolean;
  truncate?: '200';
  editable?: boolean;
}

export interface ITableGroup {
  id: string;
  header: Record<string, any>;
  items: Record<string, any>[];
  expanded?: boolean;
}

export interface ISortOutput {
  field: string;
  direction: 'asc' | 'desc';
}

export interface IInlineEditOutput {
  item: Record<string, any>;
  field: string;
  newValue: string;
  originalValue: string;
}

@Component({
    selector: 'app-content-table-cabinet',
    templateUrl: './content-table.component.html',
    styleUrl: './content-table.component.scss',
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [NgTemplateOutlet, HumanDatePipe, FormsModule, InputComponent]
})
export class ContentTableCabinetComponent implements AfterViewInit {
  columns = input<IColumnDef[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data = input<Record<string, any>[]>([]);
  groups = input<ITableGroup[]>([]);
  groupable = input<boolean>(false);

  @ContentChildren(TemplateForDirective) templateDirectives!: QueryList<TemplateForDirective>;

  private templatesMap = new Map<string, TemplateRef<unknown>>();

  // Inline editing state
  editingCell: { rowId: string | number; field: string } | null = null;
  editingValue: string = '';
  originalValue: string = '';

  onSort = output<{ field: string; direction: 'asc' | 'desc' }>();
  onInlineEdit = output<IInlineEditOutput>();
  onGroupToggle = output<{ groupId: string; expanded: boolean }>();

  // Track expanded groups
  expandedGroups: Set<string> = new Set();

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngAfterViewInit(): void {
    this.templateDirectives.forEach((dir) => this.templatesMap.set(dir.appTemplateFor(), dir.templateRef));
    this.cdr.detectChanges();
  }


  // Group expansion methods
  toggleGroup(groupId: string): void {
    const isExpanded = this.expandedGroups.has(groupId);
    if (isExpanded) {
      this.expandedGroups.delete(groupId);
    } else {
      this.expandedGroups.add(groupId);
    }
    this.onGroupToggle.emit({ groupId, expanded: !isExpanded });
  }

  isGroupExpanded(groupId: string): boolean {
    return this.expandedGroups.has(groupId);
  }

  sort(column: IColumnDef): void {
    if (!column.sortable || !column.field) return;

    // Reset other columns
    this.columns().forEach((col) => {
      if (col !== column && col.sortDirection !== undefined) col.sortDirection = undefined;
    });

    // Toggle sort direction
    column.sortDirection = column.sortDirection === 'asc' ? 'desc' : 'asc';
    this.onSort.emit({ field: column.field, direction: column.sortDirection });
  }

  formatDate(value: string): string {
    if (!value) return '';
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt.toRelative() || '' : '';
  }

  getTemplate(key: string): TemplateRef<unknown> | null {
    return this.templatesMap.get(key) || null;
  }

  // Inline editing methods
  startEditing(item: Record<string, any>, field: string): void {
    if (this.editingCell) {
      this.cancelEditing();
    }

    this.editingCell = { rowId: item['id'] as string | number, field };

    // For template columns, we need to get the actual field value
    // If the field is the same as the template field, use the item property
    // Otherwise, we might need to extract from a nested property
    this.originalValue = this.getFieldValue(item, field);
    this.editingValue = this.originalValue;

    // Focus the input after the view updates
    setTimeout(() => {
      const parent = this.elementRef.nativeElement.shadowRoot?.querySelector('.inline-edit-input');
      const input = parent?.shadowRoot?.querySelector('.if-input');
      if (parent) {
        input.focus();
      }
    });
  }

  private getFieldValue(item: Record<string, any>, field: string): string {
    // For template columns, the field might be different from the actual data property
    // For example, a template field 'name' might correspond to item.name
    // or a template field 'user' might correspond to item.email or item.fullname

    // First try direct field access
    if (item[field] !== undefined) {
      return String(item[field] || '');
    }

    // For common template field mappings, try alternative properties
    switch (field) {
      case 'name':
        return String(item['name'] || item['title'] || item['fullname'] || '');
      case 'user':
        return String(item['email'] || item['fullname'] || item['username'] || '');
      case 'description':
        return String(item['description'] || item['desc'] || '');
      default:
        return '';
    }
  }

  saveEditing(item: Record<string, any>): void {
    if (!this.editingCell || this.editingValue === this.originalValue) {
      this.cancelEditing();
      return;
    }

    // Update the item with the new value for immediate UI feedback
    this.updateItemField(item, this.editingCell.field, this.editingValue);

    this.onInlineEdit.emit({
      item,
      field: this.editingCell.field,
      newValue: this.editingValue,
      originalValue: this.originalValue
    });

    this.cancelEditing();
  }

  private updateItemField(item: Record<string, any>, field: string, value: string): void {
    // For template columns, we need to update the correct field
    // Try direct field access first
    if (Object.prototype.hasOwnProperty.call(item, field)) {
      item[field] = value;
      return;
    }

    // For common template field mappings, update the appropriate property
    switch (field) {
      case 'name':
        if (Object.prototype.hasOwnProperty.call(item, 'name')) {
          item['name'] = value;
        } else if (Object.prototype.hasOwnProperty.call(item, 'title')) {
          item['title'] = value;
        } else if (Object.prototype.hasOwnProperty.call(item, 'fullname')) {
          item['fullname'] = value;
        }
        break;
      case 'user':
        if (Object.prototype.hasOwnProperty.call(item, 'email')) {
          item['email'] = value;
        } else if (Object.prototype.hasOwnProperty.call(item, 'fullname')) {
          item['fullname'] = value;
        } else if (Object.prototype.hasOwnProperty.call(item, 'username')) {
          item['username'] = value;
        }
        break;
      case 'description':
        if (Object.prototype.hasOwnProperty.call(item, 'description')) {
          item['description'] = value;
        } else if (Object.prototype.hasOwnProperty.call(item, 'desc')) {
          item['desc'] = value;
        }
        break;
      default:
        // Fallback: try to set the field directly
        item[field] = value;
    }
  }

  cancelEditing(): void {
    this.editingCell = null;
    this.editingValue = '';
    this.originalValue = '';
  }

  isEditing(item: Record<string, any>, field: string): boolean {
    return this.editingCell?.rowId === item['id'] && this.editingCell?.field === field;
  }

  onKeyDown(event: KeyboardEvent, item: Record<string, any>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEditing(item);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditing();
    }
  }
}
